import { CommonConstant } from '@utility/constants/common';
import {
  Fragment,
  cloneElement,
  forwardRef,
  isValidElement,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { cn } from '@skupreme/shared/ui';
import { useDebouncedEffect } from '@hooks/debounceAsync';
import { AppTableRowPlaceholder } from './Placeholder';
import { TablePagination } from './TablePagination';
import type { ISortObject, ITableColumn } from 'types/common/table';
import {
  CaretDownIcon,
  CaretRightIcon,
  CaretUpIcon,
} from '@phosphor-icons/react';
import { IconSizes } from '@utility/constants/style-constants';
import {
  AppTableCell,
  type AppTableCellProps,
  AppTableHeaderCell,
  AppTableHeader,
  AppTableRow,
  type AppTableRowProps,
  AppTableBody,
  AppTableFooter,
  AppTableRoot,
} from '@components/AppTable';

const ASCENDING = CommonConstant.sortDirection.ascending;
const DESCENDING = CommonConstant.sortDirection.descending;

/*
 * Every row is its own card: the rows sit apart on the page, so each cell
 * paints the surface and the outer cells take the card's corners. The rules
 * AppTable.css draws between rows have nothing to separate here.
 */
const CARD_TABLE = cn(
  'sk-app-table--sticky',
  'sk:-mt-2 sk:border-separate sk:border-spacing-x-0 sk:border-spacing-y-2',
  'sk:[&>*>tr>*]:border-0! sk:[&>*>tr>*]:bg-surface',
  'sk:[&>*>tr>*:first-child]:rounded-l-card sk:[&>*>tr>*:last-child]:rounded-r-card',
);

interface IColumnProps {
  customProps?: Record<string, any>;
  column: ITableColumn;
  sort?: ISortObject;
  sortChanged: (key: string) => void;
}

function Columns({
  column,
  sort,
  sortChanged,
  customProps = {},
}: IColumnProps) {
  const otherPropsKey = column.componentPropsKey;
  const sortKey = column.sortKey;
  const sortable = !!sortKey && !column.hideSort;
  const direction = sortKey ? sort?.[sortKey] : undefined;
  const sorted =
    sortable && direction
      ? direction === ASCENDING
        ? 'ascending'
        : 'descending'
      : undefined;

  const onKeyDown = (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (sortKey && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      sortChanged(sortKey);
    }
  };

  const style: CSSProperties = {
    ...(column.bgcolor ? { background: column.bgcolor } : {}),
    ...(column.textcolor ? { color: column.textcolor } : {}),
    ...column.style,
  };

  return (
    <AppTableHeaderCell
      width={column.width}
      textAlign={column.textAlign || 'center'}
      sorted={sorted}
      onClick={() => sortKey && sortChanged(sortKey)}
      onKeyDown={sortable ? onKeyDown : undefined}
      tabIndex={sortable ? 0 : undefined}
      className={cn(
        'sk:text-xs sk:font-normal sk:text-fg-tertiary',
        sortable && 'sk-app-table__sortable',
        column.className,
      )}
      style={style}
    >
      {column.component
        ? cloneElement(column.component, {
            ...(column.component.props || {}),
            ...((otherPropsKey && customProps[otherPropsKey]) || {}),
          })
        : column.text}
      {sortable && (
        <span
          aria-hidden="true"
          className="sk-app-table__caret"
          data-sorted={sorted ? '' : undefined}
        >
          {sorted === 'descending' ? (
            <CaretDownIcon size={11} weight="bold" />
          ) : (
            <CaretUpIcon size={11} weight="bold" />
          )}
        </span>
      )}
    </AppTableHeaderCell>
  );
}

interface IRowProps<T> {
  item: T;
  index: number;
}

interface IAppTableProps<T = any> {
  AdditionalColumnRowComponent?: React.ReactElement;
  currentPage?: number;
  customProps?: Record<string, any>;
  columns?: Array<ITableColumn>;
  data?: Array<any>;
  descriptionText?: string;
  enableScrollToTop?: boolean;
  footerComponent?: React.ReactElement;
  headerOffSet?: number;
  HeaderRowComponent?: React.ReactElement;
  keyField?: string;
  loading?: boolean;
  loadingRows?: number;
  noDataMessage?: string | React.ReactNode;
  noShadow?: boolean;
  pageChanged?: (page: number) => void;
  paginationUpward?: boolean;
  pageSize?: number;
  pageSizeChanged?: (size: number) => void;
  RowComponent?: React.ReactElement<IRowProps<T>>;
  showHeader?: boolean;
  sortable?: boolean;
  sort?: ISortObject;
  sortChanged?: (newSort: ISortObject, key: string) => void;
  structured?: boolean;
  tableKey?: string;
  textAlign?: 'left' | 'center' | 'right';
  totalData?: number;
  totalPages?: number;
  wrapperClass?: string;
  /** Accepted for source compatibility; the rows are always bare cards. */
  basic?: boolean;
}

export function CardTable<T = any>({
  AdditionalColumnRowComponent,
  currentPage,
  customProps,
  columns = [],
  data,
  descriptionText = '',
  enableScrollToTop = false,
  footerComponent,
  headerOffSet = 0,
  HeaderRowComponent,
  keyField = '_id',
  loading,
  loadingRows = 4,
  noDataMessage = 'No Data Found',
  noShadow,
  pageChanged,
  paginationUpward = true,
  pageSize,
  pageSizeChanged,
  RowComponent,
  showHeader = true,
  sortable: _sortable,
  sort,
  sortChanged,
  structured,
  tableKey = '',
  textAlign = 'center',
  totalData,
  totalPages,
  wrapperClass,
  basic: _basic,
}: IAppTableProps<T>) {
  const myRef = useRef<HTMLDivElement | null>(null);

  const scrollToTop = () => {
    if (myRef?.current) {
      const yOffset = -80;
      const elementFromTop = myRef.current.getBoundingClientRect().top;
      const y = elementFromTop + window.pageYOffset + yOffset;

      const mainContent = document.getElementById('main-skupreme-content');
      mainContent?.scrollTo({ top: y });
    }
  };

  useDebouncedEffect(
    () => {
      if (enableScrollToTop) scrollToTop();
    },
    [data, myRef, enableScrollToTop],
    200,
  );

  const orderChanged = (key: string) => {
    let direction: string;

    if (!sort?.[key]) {
      const col = columns.find(x => x.sortKey === key);
      direction = col?.firstDirection || ASCENDING;
    } else {
      direction = sort[key] === ASCENDING ? DESCENDING : ASCENDING;
    }

    sortChanged?.({ [key]: direction } as ISortObject, key);
  };

  const showPagination = (currentPage || 0) > 0 && (totalData || 0) > 10;

  return (
    <>
      <div ref={myRef} className={cn('sk:mt-4 sk:max-w-full', wrapperClass)}>
        <AppTableRoot
          basic
          celled={structured}
          data-field-value={tableKey}
          className={cn(CARD_TABLE, showPagination && 'has-footer-table')}
          style={
            { '--sk-app-table-offset': `${headerOffSet}px` } as CSSProperties
          }
        >
          {showHeader && (
            <AppTableHeader>
              {AdditionalColumnRowComponent &&
                cloneElement(AdditionalColumnRowComponent, {
                  ...(AdditionalColumnRowComponent.props || {}),
                })}
              <AppTableRow textAlign={textAlign} verticalAlign="middle">
                {columns.map(item => (
                  <Columns
                    sort={sort}
                    customProps={customProps}
                    sortChanged={orderChanged}
                    key={item.key}
                    column={item}
                  />
                ))}
              </AppTableRow>
            </AppTableHeader>
          )}
          <AppTableBody className="table-body">
            {HeaderRowComponent &&
              cloneElement(HeaderRowComponent, {
                ...(HeaderRowComponent.props || {}),
              })}
            {loading ? (
              <AppTableRowPlaceholder
                rows={data?.length || loadingRows}
                colSpan={columns.length}
              />
            ) : (
              (data || []).map((item, index) => (
                <Fragment key={String(item[keyField])}>
                  {isValidElement(RowComponent) &&
                    cloneElement(RowComponent, {
                      ...(RowComponent.props || {}),
                      item,
                      index,
                    })}
                </Fragment>
              ))
            )}
          </AppTableBody>
          {!loading && (data || []).length === 0 && (
            <AppTableFooter>
              <AppTableRow>
                <AppTableCell
                  colSpan={columns.length || 1}
                  textAlign="center"
                  className="sk:py-8 sk:text-fg-tertiary"
                >
                  {noDataMessage}
                </AppTableCell>
              </AppTableRow>
            </AppTableFooter>
          )}
        </AppTableRoot>
      </div>
      {(footerComponent || showPagination) && (
        <TablePagination
          noPadding={!noShadow}
          colSpan={columns.length}
          currentPage={currentPage ?? 1}
          totalPages={totalPages ?? 0}
          pageChanged={pageChanged ?? (() => {})}
          pageSizeChanged={pageSizeChanged ?? (() => {})}
          pageSize={pageSize ?? 10}
          footerComponent={footerComponent}
          totalData={totalData ?? 0}
          descriptionText={descriptionText}
          upward={paginationUpward}
        />
      )}
    </>
  );
}

export type CardTableRowProps = AppTableRowProps & {
  /** Cell fill on hover; the kit's row tint when left out. */
  hoverColor?: string;
};

/**
 * Tints its cells on hover, widens a CardSideBorderTableCell's bar and slides
 * in an AccordionCardTableCell's caret.
 */
export const CardTableRow = forwardRef<HTMLTableRowElement, CardTableRowProps>(
  function CardTableRowPart({ hoverColor, className, style, ...props }, ref) {
    return (
      <AppTableRow
        ref={ref}
        {...props}
        style={
          {
            '--card-row-hover': hoverColor || 'var(--sk-rule)',
            ...style,
          } as CSSProperties
        }
        className={cn(
          'card-table-row',
          'sk:[&:hover>td]:cursor-pointer sk:[&:hover>td]:bg-[var(--card-row-hover)]',
          'sk:[&:hover_.side-border]:w-2.5',
          'sk:[&:hover_.caret-right-icon]:translate-x-0 sk:[&:hover_.caret-right-icon]:opacity-100',
          className,
        )}
      />
    );
  },
);

export const CardSideBorderTableCell = ({
  children,
  borderColor,
  className,
  ...props
}: AppTableCellProps & { borderColor?: string }) => (
  <AppTableCell
    {...props}
    className={cn('sk:relative sk:overflow-hidden', className)}
  >
    <div
      className="side-border sk:pointer-events-none sk:absolute sk:top-0 sk:left-0 sk:h-full sk:w-[5px] sk:bg-fg-tertiary sk:transition-[width] sk:duration-200"
      style={borderColor ? { backgroundColor: borderColor } : undefined}
    />
    {children}
  </AppTableCell>
);

export const AccordionCardTableCell = ({
  children,
  className,
  ...props
}: AppTableCellProps) => {
  return (
    <AppTableCell {...props} className={cn('sk:relative', className)}>
      <CaretRightIcon
        size={IconSizes.large}
        className="caret-right-icon sk:pointer-events-none sk:absolute sk:top-1/2 sk:right-2.5 sk:-translate-x-2.5 sk:-translate-y-1/2 sk:opacity-0 sk:transition-all sk:duration-200"
      />
      {children}
    </AppTableCell>
  );
};
